import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import StateContext from 'common/contexts/state-context';
import MapContext from 'common/contexts/map-context';
import { USE_WP_CHROME, OPEN_SEARCH_EVENT } from '../../GlobalValues';
import ChromeContext from './chromeContext';
import SiteHeader from './SiteHeader';
import MenuPanel from './MenuPanel';
import SearchModal from './SearchModal';
import SiteFooter from './SiteFooter';
import { searchAroundUser, searchByPlaceName, searchByPrediction } from '../../services/search';
import { WpHeader, WpFooter, useWpEmbed } from '../../widgets/WpChrome';

const MENU_ID = 'menuPanel';

/**
 * Assemble le chrome et expose `openSearch` au reste de l'application.
 *
 * Décision 2 — deux implémentations coexistent :
 *  - `USE_WP_CHROME = true` (actuel) : on rend <wp-header> / <wp-footer>, et la
 *    modale de recherche reste côté React, ouverte par l'évènement
 *    `tchip:open-search` — voir `adoptWpHeader` plus bas, qui y branche les
 *    boutons du thème ;
 *  - `USE_WP_CHROME = false` : header, frise, menu et footer sont rendus en
 *    React, ISO avec la maquette. Repli si le plugin tombe.
 *
 * La modale est montée en dernier — donc en fin de body — pour que le `<h1>` de
 * la page précède son titre dans l'ordre du document (décision 5).
 */
const Chrome = ({ children }) => {
  const [state, setState] = useContext(StateContext);
  const [map, setMap] = useContext(MapContext);
  const history = useHistory();
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isSearchOpen, setSearchOpen] = useState(false);
  const [isBusy, setBusy] = useState(false);

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const contextValue = useMemo(() => ({ openSearch, closeSearch }), [openSearch, closeSearch]);

  // Contrat avec l'équipe WordPress : le header WP ouvre la modale par évènement.
  useEffect(() => {
    const onOpen = () => setSearchOpen(true);
    document.addEventListener(OPEN_SEARCH_EVENT, onOpen);
    return () => document.removeEventListener(OPEN_SEARCH_EVENT, onOpen);
  }, []);

  useWpEmbed();

  /**
   * Trois raccords à faire une fois le header WordPress hydraté. Aucun ne
   * demande de modifier le thème ni le plugin.
   *
   * 1. `header.php` ouvre `<main id="main" class="site-main">` sur sa dernière
   *    ligne et `footer.php` le referme sur sa première. Comme le plugin pose
   *    ces deux fragments par `innerHTML` dans deux éléments distincts, le
   *    parser referme ici un `<main>` vide et jette le `</main>` orphelin du
   *    footer. Sans ce nettoyage, le document a deux `<main>` — celui-ci et
   *    celui rendu plus bas. Le décalage sous le header fixe que portait
   *    `.site-main` est, lui, déjà assuré par le `margin-top` des conteneurs de
   *    page (`storelocator.css`, `localpage.css`).
   *
   * 2. Le header WordPress embarque sa propre popin de recherche, mais le
   *    `main.js` du thème n'en câble ni le champ, ni le bouton OK, ni la
   *    géolocalisation : elle s'ouvre et ne fait rien. On la retire — deux
   *    `role="dialog"` concurrents pour la même fonction, dont un inerte.
   *
   * 3. Restent les boutons du thème en `onclick="openSearch()"` (barre du
   *    header et pied du menu burger). On réécrit la globale pour qu'ils
   *    ouvrent la modale React, seule complète.
   *
   *    Un accesseur plutôt qu'une simple affectation, parce que `main.js` est
   *    chargé **deux fois** : le header le tire par `loadScripts` (dédoublonné
   *    par handle) et le footer le réinjecte par un chemin qui ne partage pas
   *    ce cache. Sa seconde exécution repose `window.openSearch` en fin
   *    d'IIFE, et rien ne garantit l'ordre d'arrivée des deux fragments — une
   *    affectation gagnerait la course la plupart du temps, pas toujours. Le
   *    setter avale donc les réassignations. `main.js` est en `'use strict'`,
   *    mais écrire dans une propriété *qui a un setter* ne lève pas : son IIFE
   *    poursuit normalement (burger, Échap, header compact).
   */
  const adoptWpHeader = useCallback((headerElement) => {
    const strayMain = headerElement.querySelector('main.site-main');
    if (strayMain) strayMain.remove();

    const inertSearchPopin = headerElement.querySelector('#searchOverlay');
    if (inertSearchPopin) inertSearchPopin.remove();

    const requestSearch = () => document.dispatchEvent(new CustomEvent(OPEN_SEARCH_EVENT));
    Object.defineProperty(window, 'openSearch', {
      get: () => requestSearch,
      set: () => {},
      configurable: true,
    });
  }, []);

  /**
   * Une recherche depuis n'importe quelle page ramène sur le listing, et
   * recadre la carte via `map.placeLoc` (mécanisme de common / 2025_VOG).
   */
  const applyResult = (result) => {
    if (!result) return;
    setState({ ...result.state, searchOrigin: result.origin });
    if (result.origin) {
      setMap({
        ...map,
        placeId: result.placeId,
        placeLoc: { ...map.placeLoc, lat: result.origin.lat, lng: result.origin.lng },
      });
    }
    setSearchOpen(false);
    if (history.location.pathname !== '/') history.push('/');
  };

  const runSearch = async (query) => {
    setBusy(true);
    try {
      applyResult(await searchByPlaceName(state, query));
    } catch (error) {
      console.warn('La recherche a échoué', error);
    } finally {
      setBusy(false);
    }
  };

  const runGeolocation = async () => {
    setBusy(true);
    try {
      applyResult(await searchAroundUser(state));
    } catch (error) {
      console.warn('La géolocalisation a échoué', error);
    } finally {
      setBusy(false);
    }
  };

  const runSelectPrediction = async (prediction) => {
    setBusy(true);
    try {
      applyResult(await searchByPrediction(state, prediction));
    } catch (error) {
      console.warn('La recherche a échoué', error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ChromeContext.Provider value={contextValue}>
      {USE_WP_CHROME ? (
        <WpHeader onLoad={adoptWpHeader} />
      ) : (
        <SiteHeader onOpenMenu={() => setMenuOpen(true)} menuId={MENU_ID} />
      )}

      {!USE_WP_CHROME && <MenuPanel id={MENU_ID} isOpen={isMenuOpen} onClose={() => setMenuOpen(false)} />}

      <main>{children}</main>

      {USE_WP_CHROME ? <WpFooter /> : <SiteFooter />}

      <SearchModal
        isOpen={isSearchOpen}
        onClose={closeSearch}
        onSearch={runSearch}
        onSelectPrediction={runSelectPrediction}
        onGeolocate={runGeolocation}
        isBusy={isBusy}
      />
    </ChromeContext.Provider>
  );
};

export default Chrome;
